/**
 * What to re-read after the owner moves an outreach thread — a "Tell {agent}" note, or a Close — and when
 * the agent's answer to a note is actually visible. The ONE place every host of `TellAgentComposer`
 * (the thread dialog, the contact viewer, the contact thread and detail pages) and `OutreachClose` read.
 *
 * Why this exists (reported 2026-10-22): the owner answered an outreach from the Home feed's dialog; the
 * agent delivered the message 20 s later and closed the thread 22 s after that — and the Home row still read
 * "Waiting on Benjamin since 12:27 PM" after the dialog closed. The rows behind the dialog read the
 * bridge's `/api/outreaches` entry (`refreshOutreaches`) and the contact-book stage map
 * (`refreshOutreachStages`); the composer re-ran only the host's person read, and stopped as soon as the
 * NOTE settled — before the agent's send/close had landed anywhere. Each host chose its own re-read, so
 * each could be missing a different source. This set is what they all re-read now.
 *
 * Import-free on purpose (orion-web CLAUDE.md "Tests (jest)"); `lib/owner-note-rereads.ts` binds the set to
 * the real caches.
 */
import { parseOwnerNoteState, type WireOwnerEvent } from './outreach-lane';

/**
 * Every cache a row showing an outreach can be reading:
 * - `contactStages` — the contact-book list and the person / recipient reads (stage, lane, messages);
 * - `outreaches` — the bridge's `/api/outreaches` entry behind Home's rows and the task list's fold;
 * - `host` — the host's own extra read, when it has one.
 */
export type OwnerNoteSource = 'contactStages' | 'outreaches' | 'host';

export const OWNER_NOTE_REREADS: readonly OwnerNoteSource[] = ['contactStages', 'outreaches', 'host'] as const;

/**
 * Runs every source in `OWNER_NOTE_REREADS` together. A source the caller has no reader for is skipped; a
 * reader that throws or rejects does not stop the others, and the returned promise never rejects — a
 * failed re-read is retried by the next one, never surfaced.
 */
export async function runOwnerNoteRereads(readers: Partial<Record<OwnerNoteSource, (() => unknown) | undefined>>): Promise<void> {
  await Promise.allSettled(
    OWNER_NOTE_REREADS.map((source) => {
      const read = readers[source];
      if (!read) return Promise.resolve();
      try {
        return Promise.resolve(read());
      } catch (error) {
        return Promise.reject(error);
      }
    }),
  );
}

/** The slice of a thread row the visibility rule reads. Every field may be absent. */
export interface NoteThread {
  ownerEvents?: WireOwnerEvent[] | null;
  lastMessageAt?: string;
  closedAt?: string;
}

function ms(at: string | undefined): number | null {
  if (!at) return null;
  const value = Date.parse(at);
  return Number.isFinite(value) ? value : null;
}

/**
 * Thread scope: the agent's answer to note `noteId` is VISIBLE on this thread — the moment the composer may
 * stop re-reading. Settling the note is not enough on its own: a SENT note means the agent sent, but the
 * thread only shows it once its newest message (or its close) is at or after the note.
 *
 * - no TOLD event for the note yet, or the note still PENDING → false;
 * - FAILED → true (nothing more is coming);
 * - SENT / HANDLED → the thread's `lastMessageAt` or `closedAt` is at/after the note's `at` (a note with no
 *   readable `at` cannot be compared, so settling is the best available signal → true).
 *
 * A HANDLED note whose action leaves no trace on the thread keeps the composer re-reading until its
 * schedule runs out — bounded, and the dialog's close re-reads once more.
 */
export function ownerNoteActionVisible(thread: NoteThread | null | undefined, noteId: string): boolean {
  if (!noteId || !thread) return false;
  const event = (thread.ownerEvents ?? []).find((e) => e.noteId === noteId);
  if (!event) return false;
  const state = parseOwnerNoteState(event.noteState);
  if (state === 'failed') return true;
  if (state !== 'sent' && state !== 'handled') return false;
  const noteAt = ms(event.at);
  if (noteAt === null) return true;
  const moved = Math.max(ms(thread.lastMessageAt) ?? -Infinity, ms(thread.closedAt) ?? -Infinity);
  return moved >= noteAt;
}

/** Contact scope: the same rule on whichever of the contact's threads the note landed on. */
export function ownerNoteActionVisibleOnContact(
  entry: { outbound?: NoteThread[] | null; inbound?: NoteThread[] | null } | null | undefined,
  noteId: string,
): boolean {
  if (!entry || !noteId) return false;
  return [...(entry.outbound ?? []), ...(entry.inbound ?? [])].some((thread) => ownerNoteActionVisible(thread, noteId));
}
